import { useState, useEffect, useRef, useCallback } from 'react';
import { useEditorState } from '@tiptap/react';
import EmojiPicker from 'emoji-picker-react';
import HeadingDropdown from './HeadingDropdown';
import TextColorDropdown from './TextColor';

export const CustomPopover = ({ trigger, children, className = '' }) => {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  const handleClickOutside = (event) => {
    if (
      popoverRef.current &&
      !popoverRef.current.contains(event.target) &&
      triggerRef.current &&
      !triggerRef.current.contains(event.target)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleTriggerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!open);
  };

  return (
    <div className="position-relative d-inline-block">
      <div ref={triggerRef} onClick={handleTriggerClick}>
        {trigger}
      </div>
      {open && (
        <div
          ref={popoverRef}
          className={`custom-popover mt-2 ${className}`}
          onMouseDown={(e) => e.stopPropagation()}>
          {children}
        </div>
      )}
    </div>
  );
};

const Separator = ({ orientation = 'horizontal', className = '' }) => {
  if (orientation === 'horizontal') {
    return <hr className={`tiptap-separator-horizontal ${className}`} />;
  }
  return <div className={`tiptap-separator-vertical ${className}`}></div>;
};

// const TextStyleDropdown = ({ editor, editorState }) => {
//   const textStyles = [
//     {
//       label: 'Paragraph',
//       value: 'paragraph',
//       action: () => editor.chain().focus().setParagraph().run(),
//       isActive: editorState.isParagraph,
//       icon: 'fas fa-paragraph'
//     },
//     {
//       label: 'Heading 1',
//       value: 'h1',
//       action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
//       isActive: editorState.isHeading1,
//       icon: 'fas fa-heading',
//       className: 'text-2xl font-bold'
//     },
//     {
//       label: 'Heading 2',
//       value: 'h2',
//       action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
//       isActive: editorState.isHeading2,
//       icon: 'fas fa-heading',
//       className: 'text-xl font-bold'
//     },
//     {
//       label: 'Heading 3',
//       value: 'h3',
//       action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
//       isActive: editorState.isHeading3,
//       icon: 'fas fa-heading',
//       className: 'text-lg font-bold'
//     },
//     {
//       label: 'Heading 4',
//       value: 'h4',
//       action: () => editor.chain().focus().toggleHeading({ level: 4 }).run(),
//       isActive: editorState.isHeading4,
//       icon: 'fas fa-heading',
//       className: 'text-base font-bold'
//     },
//     {
//       label: 'Heading 5',
//       value: 'h5',
//       action: () => editor.chain().focus().toggleHeading({ level: 5 }).run(),
//       isActive: editorState.isHeading5,
//       icon: 'fas fa-heading',
//       className: 'text-sm font-bold'
//     },
//     {
//       label: 'Heading 6',
//       value: 'h6',
//       action: () => editor.chain().focus().toggleHeading({ level: 6 }).run(),
//       isActive: editorState.isHeading6,
//       icon: 'fas fa-heading',
//       className: 'text-xs font-bold'
//     }
//   ];

//   const getCurrentStyle = () => {
//     const activeStyle = textStyles.find((style) => style.isActive);
//     return activeStyle ? activeStyle.label : 'Paragraph';
//   };

//   const handleStyleSelect = (style, setOpen) => {
//     style.action();
//     setOpen(false);
//   };

//   return (
//     <CustomPopover
//       trigger={
//         <button type="button" className="toolbar-btn ">
//           <span className="text-truncate" style={{ maxWidth: '80px' }}>
//             {getCurrentStyle()}
//           </span>
//           <i className="fas fa-chevron-down text-xs"></i>
//         </button>
//       }
//       className="text-style-dropdown">
//       <div className=" p-1">
//         {textStyles.map((style) => (
//           <button type="button"
//             key={style.value}
//             onClick={() => handleStyleSelect(style)}
//             className={`d-flex align-items-center gap-2 w-100 text-start ${
//               style.isActive ? 'active' : ''
//             }`}>
//             <span className={style.className || ''}>{style.label}</span>
//             {style.isActive && <i className="fas fa-check ms-auto"></i>}
//           </button>
//         ))}
//       </div>
//     </CustomPopover>
//   );
// };

const ListControls = ({ editor, editorState }) => {
  const isList = editorState.isOrderedList || editorState.isBulletList;

  return (
    <>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`toolbar-btn ${editorState.isBulletList ? 'active' : ''}`}
        title="Bullet List">
        <i className="fas fa-list-ul"></i>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`toolbar-btn ${editorState.isOrderedList ? 'active' : ''}`}
        title="Numbered List">
        <i className="fas fa-list-ol"></i>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
        className="toolbar-btn"
        disabled={!isList}
        title="Increase Indent">
        <i className="fas fa-indent"></i>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().liftListItem('listItem').run()}
        className="toolbar-btn"
        disabled={!isList}
        title="Decrease Indent">
        <i className="fas fa-outdent"></i>
      </button>
    </>
  );
};

const FormattingControls = ({ editor, editorState }) => {
  return (
    <>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editorState.canBold}
        className={`toolbar-btn ${editorState.isBold ? 'active' : ''}`}
        title="Bold">
        <i className="fas fa-bold"></i>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editorState.canItalic}
        className={`toolbar-btn ${editorState.isItalic ? 'active' : ''}`}
        title="Italic">
        <i className="fas fa-italic"></i>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={!editorState.canUnderline}
        className={`toolbar-btn ${editorState.isUnderline ? 'active' : ''}`}
        title="Underline">
        <i className="fas fa-underline"></i>
      </button>
      <Separator orientation="vertical" />
      <TextColorDropdown editor={editor} />
      <HeadingDropdown editor={editor} />
      {/* <button type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editorState.canStrike}
        className={`toolbar-btn ${editorState.isStrike ? 'active' : ''}`}
        title="Strikethrough">
        <i className="fas fa-strikethrough"></i>
      </button>
      <button type="button"
        onClick={() => editor.chain().focus().toggleCode().run()}
        disabled={!editorState.canCode}
        className={`toolbar-btn ${editorState.isCode ? 'active' : ''}`}
        title="Inline Code">
        <i className="fas fa-code"></i>
      </button> */}
    </>
  );
};

function MenuBar({ editor, emojiPosition = '' }) {
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isBold: ctx.editor.isActive('bold') ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive('italic') ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isUnderline: ctx.editor.isActive('underline') ?? false,
        canUnderline: ctx.editor.can().chain().toggleUnderline().run() ?? false,
        isStrike: ctx.editor.isActive('strike') ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
        isParagraph: ctx.editor.isActive('paragraph') ?? false,
        isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
        isHeading4: ctx.editor.isActive('heading', { level: 4 }) ?? false,
        isHeading5: ctx.editor.isActive('heading', { level: 5 }) ?? false,
        isHeading6: ctx.editor.isActive('heading', { level: 6 }) ?? false,
        isBulletList: ctx.editor.isActive('bulletList') ?? false,
        isOrderedList: ctx.editor.isActive('orderedList') ?? false,
        isBlockquote: ctx.editor.isActive('blockquote') ?? false,
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false
      };
    }
  });

  const onEmojiClick = useCallback(
    (emojiData) => {
      if (editor) {
        editor.chain().focus().insertContent(emojiData.emoji).run();
      }
    },
    [editor]
  );

  return (
    <div className="control-group">
      <div className="toolbar d-flex bg-grey gap-1 p-2 flex-wrap">
        {/* Text Style Dropdown */}
        {/* <TextStyleDropdown editor={editor} editorState={editorState} /> */}

        {/* <Separator orientation="vertical" /> */}

        {/* Formatting Controls */}
        <FormattingControls editor={editor} editorState={editorState} />

        <Separator orientation="vertical" />

        {/* List Controls */}
        <ListControls editor={editor} editorState={editorState} />

        <Separator orientation="vertical" />

        {/* Additional Controls */}
        {/* <AdditionalControls editor={editor} editorState={editorState} /> */}

        {/* <Separator orientation="vertical" /> */}

        {/* Emoji Picker */}
        <CustomPopover
          trigger={
            <button type="button" className="toolbar-btn" title="Insert Emoji">
              <i className="far fa-smile"></i>
            </button>
          }
          className="w-auto border-none">
          <div
            className={
              emojiPosition === 'left' ? 'custom-emoji-popover-left' : 'custom-emoji-popover'
            }>
            <EmojiPicker
              className="pe-3"
              previewConfig={{ showPreview: false }}
              onEmojiClick={onEmojiClick}
              height={'400px'}
              width={'320px'}
              style={{
                '--epr-search-background-color': '#f8f9fa',
                '--epr-search-border-color': '#dee2e6',
                '--epr-search-text-color': '#495057',
                '--epr-search-margin': '0px'
              }}
              skinTonesDisabled
            />
          </div>
        </CustomPopover>

        {/* Clear Formatting */}
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetAllMarks().run()}
          disabled={!editorState.canClearMarks}
          className="toolbar-btn"
          title="Clear Formatting">
          <i className="fas fa-remove-format"></i>
        </button>
      </div>
    </div>
  );
}

export default MenuBar;
